import { TableCheckbox } from '@/components/ui/table-checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { CellContext, HeaderContext } from '@tanstack/react-table'
import type { ReactElement } from 'react'
import type { ExtendedCellContext } from './types'

function WithTooltip({ message, children }: { message: string | null | undefined; children: ReactElement }) {
	if (!message) {
		return children
	}
	return (
		<Tooltip>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent side='right' sideOffset={8}>
				<p>{message}</p>
			</TooltipContent>
		</Tooltip>
	)
}

/**
 * Module level so their identity never changes: flexRender mounts a column's header and cell as
 * components, and a new function per render remounted every checkbox and dropped keyboard focus.
 *
 * Both toggle on pointerup, not click: a fast click rerenders the table and moves the cell between
 * mousedown and mouseup, so the browser fires no `click`, nor the checkbox `change` that rides on it.
 * Clicks stop at the wrapper, so the row's click handler and the table's click away never see them.
 */
export function SelectAllHeader<TData>({ table }: HeaderContext<TData, unknown>) {
	const {
		selectionState,
		selectionMode,
		isMobile,
		disabledRowTooltip,
		disableSelectAll,
		selectAllDisabledTooltip,
		dataTestId,
	} = table.options.meta?.selectColumn ?? {}
	if (!selectionState || selectionMode === 'single') {
		return null
	}
	// No row is selectable (RBAC denies them all) or no bulk action exists, so a live toggle would mislead.
	const selectAllDisabled = !selectionState.hasSelectableRows || disableSelectAll
	// A select all served by the backend is in flight: show the wait and ignore toggles until it lands.
	const selectAllPending = selectionState.isSelectingAll
	// Only a plain string works here: a tooltip function needs a row, and the header has none.
	const tooltipMessage = selectAllDisabled
		? disableSelectAll
			? selectAllDisabledTooltip
			: typeof disabledRowTooltip === 'string'
				? disabledRowTooltip
				: null
		: null

	const checkbox = (
		// biome-ignore lint/a11y/useSemanticElements: the native checkbox is visual only; the wrapper takes the pointer and the keyboard
		<div
			role='checkbox'
			aria-checked={selectionState.isPartiallySelected ? 'mixed' : selectionState.isAllSelected}
			aria-disabled={selectAllDisabled}
			aria-busy={selectAllPending}
			aria-label='Select all rows'
			tabIndex={selectAllDisabled ? -1 : 0}
			data-header-checkbox='true'
			data-testid={dataTestId ? `${dataTestId}-select-all` : undefined}
			className={cn(
				'flex items-center justify-center group/checkbox focus:outline-none',
				selectAllDisabled ? 'cursor-not-allowed' : selectAllPending ? 'cursor-wait' : 'cursor-pointer',
				selectAllDisabled || isMobile || selectAllPending || selectionState.selectedCount > 0
					? 'opacity-100'
					: 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
			)}
			onPointerUp={event => {
				if (event.button !== 0 || selectAllDisabled || selectAllPending) {
					return
				}
				event.stopPropagation()
				selectionState.toggleAll()
			}}
			onClick={event => event.stopPropagation()}
			onKeyDown={event => {
				if (event.key === ' ' || event.key === 'Enter') {
					event.preventDefault()
					event.stopPropagation()
					if (!selectAllDisabled && !selectAllPending) {
						selectionState.toggleAll()
					}
				}
			}}>
			<TableCheckbox
				checked={selectionState.isAllSelected}
				indeterminate={selectionState.isPartiallySelected}
				readOnly
				tabIndex={-1}
				aria-hidden='true'
				disabled={selectAllDisabled}
				className='pointer-events-none group-focus-visible/checkbox:outline-2 group-focus-visible/checkbox:outline-offset-2 group-focus-visible/checkbox:outline-primary'
			/>
		</div>
	)

	return (
		<div className='flex items-center justify-center group w-full'>
			<WithTooltip message={tooltipMessage}>{checkbox}</WithTooltip>
		</div>
	)
}

export function SelectRowCell<TData>({ row, table, ...context }: CellContext<TData, unknown>) {
	const { selectionState, getRowId, selectionMode, disabledRowTooltip, dataTestId } =
		table.options.meta?.selectColumn ?? {}
	if (!selectionState) {
		return null
	}
	const rowId = getRowId ? getRowId(row.original) : row.id
	const isSelectable = selectionState.isRowSelectable(row.original)
	const rowIndex = (context as ExtendedCellContext).virtualIndex ?? row.index
	const isRowChecked = selectionState.isSelected(rowId)
	const tooltipMessage =
		!isSelectable && disabledRowTooltip
			? typeof disabledRowTooltip === 'function'
				? disabledRowTooltip(row.original)
				: disabledRowTooltip
			: null

	const handleSelectToggle = (shiftPressed: boolean): void => {
		if (!isSelectable) {
			return
		}
		if (selectionMode === 'single') {
			if (isRowChecked) {
				selectionState.deselectRow(rowId)
			} else {
				selectionState.deselectAll()
				selectionState.selectRow(rowId, row.original)
			}
		} else {
			selectionState.toggleRow(rowId, row.original, rowIndex, shiftPressed)
		}
	}

	const checkbox = (
		// biome-ignore lint/a11y/useSemanticElements: the native checkbox is visual only; the wrapper takes the pointer and the keyboard
		<div
			role='checkbox'
			aria-checked={isRowChecked}
			aria-label='Select row'
			aria-disabled={!isSelectable}
			tabIndex={isSelectable ? 0 : -1}
			data-testid={dataTestId ? `${dataTestId}-row-checkbox-${rowId}` : undefined}
			className={cn(
				'flex items-center justify-center size-full group/checkbox focus:outline-none',
				isSelectable ? 'cursor-pointer' : 'cursor-not-allowed',
			)}
			onPointerUp={event => {
				if (event.button !== 0) {
					return
				}
				event.stopPropagation()
				handleSelectToggle(event.shiftKey)
			}}
			onClick={event => event.stopPropagation()}
			// Rapid toggling must not open the row, which listens for double clicks.
			onDoubleClick={event => event.stopPropagation()}
			onKeyDown={event => {
				if (event.key === ' ' || event.key === 'Enter') {
					event.preventDefault()
					event.stopPropagation()
					handleSelectToggle(event.shiftKey)
				}
			}}>
			<TableCheckbox
				checked={isRowChecked}
				readOnly
				tabIndex={-1}
				aria-hidden='true'
				disabled={!isSelectable}
				className='pointer-events-none group-focus-visible/checkbox:outline-2 group-focus-visible/checkbox:outline-offset-2 group-focus-visible/checkbox:outline-primary'
			/>
		</div>
	)

	return (
		<div className='flex items-center justify-center size-full'>
			<WithTooltip message={tooltipMessage}>{checkbox}</WithTooltip>
		</div>
	)
}
